import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import {
    Tab as BaseTab,
    Tabs as BaseTabs,
    TabList,
    TabPanel,
} from "react-aria-components";

import sassVariables from "../../styles/variables.module.scss";
import styles from "./Tabs.module.scss";

interface Props extends React.ComponentProps<typeof BaseTabs> {
    /** Alignment of row of tab switching buttons */
    tabBarAlignment?: "left" | "center";
    tabs: React.ReactNode;
    panels: React.ReactNode;
}

interface CustomCss extends React.CSSProperties {
    "--width": string;
    "--left": string;
}

function useTabsRect(
    ref: React.RefObject<HTMLElement>,
): [left: number, width: number, containerWidth: number] {
    const [result, setResult] = useState<
        [left: number, width: number, containerWidth: number]
    >([0, 0, 0]);

    useEffect(() => {
        const root = ref.current;
        if (!root) return;

        function updateDimensions() {
            const tabList = ref.current;
            const activeTab = tabList?.querySelector('[data-selected="true"]');
            if (!tabList || !activeTab) {
                setResult([0, 0, 0]);
                return;
            }

            const listRect = tabList.getBoundingClientRect();
            const tabRect = activeTab.getBoundingClientRect();

            setResult([
                tabRect.left - listRect.left,
                tabRect.width,
                listRect.width,
            ]);
        }
        const observer = new ResizeObserver(updateDimensions);
        window.requestAnimationFrame(() => updateDimensions());
        observer.observe(root);

        return () => {
            observer.disconnect();
        };
    }, [ref]);

    return result;
}

export function Tabs({
    tabBarAlignment = "center",
    tabs,
    panels,
    ...props
}: Props) {
    const ref = useRef<HTMLDivElement>(null);
    const [left, width, containerWidth] = useTabsRect(ref);

    return (
        <BaseTabs
            ref={ref}
            {...props}
            className={classnames([styles.root, props.className])}
        >
            <TabList
                className={classnames(styles.buttonBar, {
                    [styles.alignLeft]: tabBarAlignment === "left",
                    [styles.alignCenter]: tabBarAlignment === "center",
                    [styles.small]:
                        containerWidth <
                        parseInt(sassVariables.breakpointMobile),
                })}
                style={
                    {
                        "--left": left + "px",
                        "--width": width + "px",
                    } as CustomCss
                }
            >
                {tabs}
            </TabList>

            {panels}
        </BaseTabs>
    );
}

export function Tab({
    className,
    ...props
}: React.ComponentProps<typeof BaseTab>) {
    return (
        <BaseTab
            {...props}
            className={classnames([styles.button, className])}
        />
    );
}

export { TabPanel };
